import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AddressBook.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse, faBuilding, faClipboardList, faLocationDot, faPen, faTrashCan, faPhone, faInbox } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import BackButton from '../../components/BackButton/BackButton';

type AddressTab = 'Home' | 'Office' | 'Other';
type Address = { name: string; relation: string; address: string; phone: string , isDefault : boolean;};

const AddressBook: React.FC = () => {
  const { t } = useTranslation(['profile', 'common']);
  const [activeAddressTab, setActiveAddressTab] = useState<AddressTab>('Home');
  const [addressBookData] = useState<Record<AddressTab, Address[]>>({
    Home: [
      { name: 'John Doe', relation: 'Self', address: '123 Main Street, Apartment 4B\nBangalore, Karnataka 560001', phone: '+91 9876543210', isDefault: false},
    ],
    Office: [],
    Other: [],
  });
  const [addressFilter, setAddressFilter] = useState<string>('');
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);

  const addressTabs: { key: AddressTab; icon: IconDefinition; color: string }[] = [
    { key: 'Home', icon: faHouse, color: '#06b6d4' },
    { key: 'Office', icon: faBuilding, color: '#8b5cf6' },
    { key: 'Other', icon: faClipboardList, color: '#f59e0b' },
  ];

  const getTabColor = (tab: AddressTab) => {
    const found = addressTabs.find(t => t.key === tab);
    return found ? found.color : '#06b6d4';
  };

  const getTabIcon = (tab: AddressTab) => {
    const found = addressTabs.find(t => t.key === tab);
    return found ? found.icon : faLocationDot;
  };

  const totalAddresses = Object.values(addressBookData).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="ab-page">
      <main className="ab-main">
        {/* Mini Hero Section */}
        <section className="ab-hero-section">
          <div className="ab-hero-inner">
            <BackButton />
            <div className="ab-hero-layout">
              <div className="ab-hero-text">
                <div className="ab-hero-badge">
                  <FontAwesomeIcon icon={faLocationDot} className="ab-hero-badge-icon" />
                  Addresses
                </div>
                <h1 className="ab-hero-title">Address Book</h1>
                <p className="ab-hero-subtitle">
                  Manage your saved addresses for quick access during bookings, deliveries, and appointments.
                </p>
              </div>
              <div className="ab-hero-stats">
                <div className="ab-hero-stat">
                  <span className="ab-hero-stat-icon"><FontAwesomeIcon icon={faLocationDot} /></span>
                  <span className="ab-hero-stat-value">{totalAddresses}</span>
                  <span className="ab-hero-stat-label">Total</span>
                </div>
                <div className="ab-hero-stat">
                  <span className="ab-hero-stat-icon"><FontAwesomeIcon icon={faHouse} /></span>
                  <span className="ab-hero-stat-value">{addressBookData.Home.length}</span>
                  <span className="ab-hero-stat-label">Home</span>
                </div>
                <div className="ab-hero-stat">
                  <span className="ab-hero-stat-icon"><FontAwesomeIcon icon={faBuilding} /></span>
                  <span className="ab-hero-stat-value">{addressBookData.Office.length}</span>
                  <span className="ab-hero-stat-label">Office</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Area */}
        <div className="ab-content-area">
          {/* Tab Navigation */}
          <div className="ab-tabs-container">
            {addressTabs.map(tab => (
              <button
                key={tab.key}
                className={`ab-tab-btn${activeAddressTab === tab.key ? ' active' : ''}`}
                style={activeAddressTab === tab.key ? { '--tab-color': tab.color } as React.CSSProperties : undefined}
                onClick={() => setActiveAddressTab(tab.key)}
              >
                <span className="ab-tab-icon"><FontAwesomeIcon icon={tab.icon} /></span>
                {tab.key}
                <span className="ab-tab-count">{addressBookData[tab.key].length}</span>
              </button>
            ))}
          </div>

          {/* Section Header */}
          <div className="ab-section-header" style={{ borderColor: getTabColor(activeAddressTab) + '40' }}>
            <div className="ab-section-header-text">
              <h2>
                <span className="ab-section-header-icon">
                  <FontAwesomeIcon icon={getTabIcon(activeAddressTab)} />
                </span>
                {activeAddressTab} Addresses
              </h2>
              <p>View and manage your {activeAddressTab.toLowerCase()} addresses below.</p>
            </div>
            <div className="ab-section-header-actions">
              <select
                className="ab-filter-select"
                value={addressFilter}
                onChange={e => setAddressFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="self">Self</option>
                <option value="others">Others</option>
              </select>
              <button className="ab-add-btn" onClick={() => setShowAddAddressForm(true)}>
                <span>+</span> Add Address
              </button>
            </div>
          </div>

          {/* Address Cards Grid */}
          <div className="ab-cards-grid">
            {addressBookData[activeAddressTab].length === 0 ? (
              <div className="ab-empty-state">
                <div className="ab-empty-icon"><FontAwesomeIcon icon={faInbox} /></div>
                <p>No {activeAddressTab.toLowerCase()} addresses found.</p>
                <button className="ab-empty-btn" onClick={() => setShowAddAddressForm(true)}>
                  + Add {activeAddressTab} Address
                </button>
              </div>
            ) : (
              addressBookData[activeAddressTab]
                .filter((addr: Address) => {
                  if (addressFilter === 'self') return addr.relation.toLowerCase() === 'self';
                  if (addressFilter === 'others') return addr.relation.toLowerCase() !== 'self';
                  return true;
                })
                .map((addr: Address, idx: number) => (
                  <div
                    className="ab-card"
                    key={idx}
                    style={{ borderLeftColor: getTabColor(activeAddressTab) }}
                  >
                    <div className="ab-card-header">
                      <div className="ab-card-name">{addr.name}</div>
                      <span className="ab-card-relation" style={{ background: getTabColor(activeAddressTab) + '18', color: getTabColor(activeAddressTab) }}>
                        {addr.relation}
                      </span>
                    </div>
                    <div className="ab-card-content">
                      <div className="ab-card-address">{addr.address}</div>
                      <div className="ab-card-phone" style={{ color: getTabColor(activeAddressTab) }}>
                        <FontAwesomeIcon icon={faPhone} className="ab-phone-icon" />
                        {addr.phone}
                      </div>
                    </div>
                    <div className="ab-card-actions">
                      <button className="ab-action-btn ab-action-edit" style={{ borderColor: getTabColor(activeAddressTab), color: getTabColor(activeAddressTab), background: getTabColor(activeAddressTab) + '10' }}>
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button className="ab-action-btn ab-action-delete">
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                  </div>
                ))
            )}

            {/* Add New Address Card */}
            <div className="ab-card ab-add-card" onClick={() => setShowAddAddressForm(true)}>
              <div className="ab-add-card-inner">
                <div className="ab-add-icon">+</div>
                <div className="ab-add-text">Add New Address</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddressBook;
